import type { ReactNode } from 'react';
import { formatNumber } from '@/lib/format';
import { cn } from '@/lib/utils';

/**
 * I tre blocchi della home.
 *
 * Rispondono alle tre domande che un operatore si fa aprendo la console, e in
 * quest'ordine: **quanti mi seguono**, **quanto ho pubblicato**, **quanto
 * posso ancora notificare**. Prima c'era un blocco solo — il budget — cioè la
 * risposta alla terza, che è quella che serve per ultima.
 *
 * Nessun grafico: sarebbe una linea inventata. Il seguito di un Comune non ha
 * una serie storica (`analytics_daily` fotografa l'Italia, non i singoli enti),
 * e disegnare una curva su un punto solo è il modo più elegante di mentire.
 * Quando la serie esisterà, il posto è questo e il numero grande resta dov'è.
 */

/* -------------------------------------------------------------------------- */
/* Il blocco                                                                  */
/* -------------------------------------------------------------------------- */

export function AnalyticsGrid({ children }: { children: ReactNode }) {
  return <div className="grid gap-4 sm:grid-cols-3">{children}</div>;
}

/**
 * Un riquadro: etichetta piccola in alto, il dato sotto. Nient'altro.
 *
 * La struttura è identica in tutti e tre — stessa altezza, stesso ritmo — così
 * l'occhio confronta i numeri invece di ricostruire ogni volta il layout.
 *
 * Le note esplicative sono uscite di qui: tre paragrafi di corpo 13 sotto tre
 * numeri fanno un muro di testo che si smette di leggere al secondo giorno, e
 * a quel punto non spiegano più niente — occupano solo lo spazio che dovrebbe
 * far respirare i numeri. Quello che davvero non si può non dire sta altrove e
 * dove serve: l'unità accanto alla cifra, e i vincoli in un `Alert` che compare
 * solo quando scattano.
 */
export function AnalyticsBlock({
  label,
  children,
  tone = 'default',
}: {
  label: string;
  children: ReactNode;
  tone?: 'default' | 'danger';
}) {
  return (
    <section
      className={cn(
        'flex flex-col rounded-xl border bg-card px-4 py-4',
        tone === 'danger' && 'border-rossa/40 bg-rossa/5',
      )}
    >
      {/* Due righe riservate anche quando l'etichetta ne occupa una: senza,
          «Notifiche disponibili oggi» va a capo e il suo numero grande scende
          rispetto a quello del riquadro accanto. Tre numeri che non stanno
          sulla stessa linea si confrontano peggio, e la riga smette di
          leggersi come una riga. */}
      <h3 className="min-h-[2.4em] text-[13px] font-semibold uppercase leading-tight tracking-[0.04em] text-ink4">
        {label}
      </h3>
      <div className="mt-3 flex-1">{children}</div>
    </section>
  );
}

/* -------------------------------------------------------------------------- */
/* I dati dentro                                                              */
/* -------------------------------------------------------------------------- */

/** Il numero grande. `unknown` per il dato che il server non ci ha mandato. */
export function BigNumber({
  value,
  unit,
  tone = 'default',
}: {
  value: number | null;
  unit: string;
  tone?: 'default' | 'danger';
}) {
  return (
    <p className="flex items-baseline gap-1.5">
      <span
        className={cn(
          'tnum text-[34px] font-bold leading-none tracking-[-0.02em]',
          tone === 'danger' ? 'text-rossa' : 'text-ink',
          value === null && 'text-ink4',
        )}
      >
        {value === null ? '—' : formatNumber(value)}
      </span>
      <span className="text-[14px] font-medium text-ink3">{unit}</span>
    </p>
  );
}

/**
 * Tre numeri in colonna: oggi, la settimana, il mese.
 *
 * In colonna e non affiancati perché sono una progressione, non tre misure
 * indipendenti: il mese CONTIENE la settimana, che contiene oggi. Affiancarli
 * li farebbe leggere come tre categorie che si sommano, che è la lettura
 * sbagliata — e la barra sotto ciascuno lo dice senza scriverlo, perché è
 * lunga in proporzione al più grande dei tre.
 */
export function Progression({
  righe,
}: {
  righe: { label: string; value: number | null }[];
}) {
  const max = Math.max(1, ...righe.map((r) => r.value ?? 0));
  return (
    <div className="space-y-2.5">
      {righe.map((r) => (
        <div key={r.label}>
          <div className="flex items-baseline justify-between gap-2">
            <span className="text-[14px] text-ink2">{r.label}</span>
            <span className="tnum text-[17px] font-semibold text-ink">
              {r.value === null ? '—' : formatNumber(r.value)}
            </span>
          </div>
          <div className="mt-1 h-1.5 overflow-hidden rounded-full bg-muted">
            <div
              className="h-full rounded-full bg-primary/70 transition-all"
              style={{ width: `${((r.value ?? 0) / max) * 100}%` }}
            />
          </div>
        </div>
      ))}
    </div>
  );
}

/**
 * Le push che restano, per finestra.
 *
 * Due righe e non un numero solo, perché i tetti sono due e chi ne ha esaurito
 * uno solo non lo scoprirebbe: si può avere «4 oggi» e zero sulla settimana, e
 * la notifica non parte lo stesso.
 *
 * **Quadratini e non una barra**, a differenza degli avvisi pubblicati qui
 * accanto: là si misura una quantità che cresce e non ha un tetto, e una barra
 * proporzionale è la forma giusta; qui si contano gettoni che si spendono, e
 * sei caselle di cui due spente si leggono senza dover interpretare una
 * lunghezza. La differenza di forma dice anche che i due riquadri misurano
 * cose diverse, che una barra uguale in tutti e due nasconderebbe.
 *
 * Oltre i dodici si torna alla barra: a quel punto i quadratini non si contano
 * più a colpo d'occhio, e diventano una barra tratteggiata che finge di essere
 * numerabile.
 */
export function Disponibili({
  righe,
}: {
  righe: { label: string; restanti: number; cap: number }[];
}) {
  return (
    <div className="space-y-2.5">
      {righe.map((r) => {
        const finito = r.restanti <= 0;
        return (
          <div key={r.label}>
            <div className="flex items-baseline justify-between gap-2">
              <span className="text-[14px] text-ink2">{r.label}</span>
              <span
                className={cn(
                  'tnum text-[17px] font-semibold',
                  finito ? 'text-rossa' : 'text-ink',
                )}
              >
                {r.restanti}
                <span className="text-[14px] font-normal text-ink3">
                  /{r.cap}
                </span>
              </span>
            </div>
            <div className="mt-1.5">
              {r.cap <= 12 ? (
                <Gettoni restanti={r.restanti} cap={r.cap} />
              ) : (
                <div className="h-1.5 overflow-hidden rounded-full bg-muted">
                  <div
                    className={cn(
                      'h-full rounded-full transition-all',
                      finito ? 'bg-rossa' : 'bg-primary/70',
                    )}
                    style={{
                      width: `${(Math.max(0, r.restanti) / r.cap) * 100}%`,
                    }}
                  />
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

/** I gettoni: pieni quelli che restano, spenti quelli già spesi. */
function Gettoni({ restanti, cap }: { restanti: number; cap: number }) {
  return (
    <div className="flex flex-wrap gap-1" aria-hidden="true">
      {Array.from({ length: cap }, (_, i) => (
        <span
          key={i}
          className={cn(
            'h-2 flex-1 rounded-full',
            i < restanti ? 'bg-primary' : 'bg-muted',
          )}
        />
      ))}
    </div>
  );
}
